import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import { Modal, Button, Input } from '../common';
import { useCreateGame } from '../../hooks/useGames';

export const CreateGameModal = ({ isOpen, onClose }) => {
  const [showAdvanced, setShowAdvanced] = useState(false);
  const { mutateAsync: createGame, isPending } = useCreateGame();
  const [error, setError] = useState('');

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm({
    defaultValues: {
      settings: {
        maxPlayers: 6,
        roundDurationHours: 48,
        defaultPassageLength: 2,
        startingInfluence: 5,
      },
    },
  });

  const onSubmit = async (data) => {
    setError('');
    try {
      await createGame(data);
      reset();
      onClose();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to create game');
    }
  };

  const handleClose = () => {
    reset();
    setError('');
    onClose();
  };

  return (
    <Modal isOpen={isOpen} onClose={handleClose} title="Create New Game" size="lg">
      {error && (
        <div className="bg-red-50 text-red-600 px-4 py-3 rounded-lg mb-4">
          {error}
        </div>
      )}

      <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
        <Input
          label="Title *"
          {...register('title', {
            required: 'Title is required',
            minLength: { value: 3, message: 'Title must be at least 3 characters' },
          })}
          error={errors.title?.message}
          placeholder="The Dragon's Gambit"
        />

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Description
          </label>
          <textarea
            {...register('description')}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
            rows={3}
            placeholder="A tale of intrigue in a fantasy kingdom..."
          />
        </div>

        <button
          type="button"
          onClick={() => setShowAdvanced(!showAdvanced)}
          className="text-sm text-primary-600 hover:underline flex items-center gap-1"
        >
          {showAdvanced ? '- Hide' : '+ Show'} Session Zero Details
        </button>

        {showAdvanced && (
          <div className="space-y-4 p-4 bg-gray-50 rounded-lg">
            <Input
              label="Intended Outcome"
              {...register('outcome')}
              placeholder="Short story, Graphic novel, etc."
            />

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Setting
              </label>
              <textarea
                {...register('premise.setting')}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
                rows={2}
                placeholder="Medieval fantasy kingdom of Valdris..."
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Situation
              </label>
              <textarea
                {...register('premise.situation')}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
                rows={2}
                placeholder="The king has been poisoned..."
              />
            </div>

            <Input
              label="Tone"
              {...register('premise.tone')}
              placeholder="Dark political fantasy"
            />

            <hr className="border-gray-200" />

            <div className="grid grid-cols-2 gap-4">
              <Input
                label="Max Players"
                type="number"
                {...register('settings.maxPlayers', { valueAsNumber: true })}
              />
              <Input
                label="Round Duration (hours)"
                type="number"
                {...register('settings.roundDurationHours', { valueAsNumber: true })}
              />
              <Input
                label="Default Passage Length"
                type="number"
                {...register('settings.defaultPassageLength', { valueAsNumber: true })}
              />
              <Input
                label="Starting Influence"
                type="number"
                {...register('settings.startingInfluence', { valueAsNumber: true })}
              />
            </div>
          </div>
        )}

        <div className="flex justify-end gap-3 pt-4">
          <Button type="button" variant="secondary" onClick={handleClose}>
            Cancel
          </Button>
          <Button type="submit" loading={isPending}>
            Create Game
          </Button>
        </div>
      </form>
    </Modal>
  );
};
